import { html, type TemplateResult } from "lit-html";
import type { AppState } from "../data/store";
import { store } from "../data/store";
import type { BuyerRow, Template } from "../data/types";
import { dedupeForBulk, type DedupeResult } from "../utils/format";

/**
 * Floating bulk-action bar — appears when ≥1 buyer rows are selected.
 * Pick a template, click Send, system fires off replies in sequence
 * with throttling to respect Resend's rate limit.
 *
 * Renders nothing when nothing is selected AND no bulk-status banner
 * is sticky. After a send finishes, the status banner stays visible
 * for ~6s so the operator sees the "N sent / M failed" summary.
 *
 * Phase 2.T — cross-account dedupe: before sending we group selected
 * buyers by phone (or normalized name) and pick one primary per
 * person. Operator sees "Send to N (X dupes skipped)" and can opt
 * out via the secondary "Send to all anyway" link.
 */
export function renderBulkActionBar(state: AppState, variant: "desktop" | "mobile"): TemplateResult {
  const count   = state.selectedConvIds.size;
  const status  = state.bulkStatus;
  const visible = count > 0 || status !== null;
  if (!visible) return html``;

  const cls = variant === "mobile" ? "bulk-bar bulk-bar--mobile" : "bulk-bar";

  if (status?.sending) {
    return html`
      <div class="${cls} bulk-bar--sending">
        <span class="bulk-bar__spinner">⟳</span>
        Sending ${status.sent + status.failed} / ${status.total}…
        ${status.failed > 0 ? html` <span class="bulk-bar__failed">${status.failed} failed</span>` : ""}
      </div>
    `;
  }
  if (status && !status.sending) {
    const ok = status.sent;
    const bad = status.failed;
    return html`
      <div class="${cls} ${bad > 0 ? "bulk-bar--mixed" : "bulk-bar--done"}">
        ${bad > 0
          ? html`Sent ${ok} · ${bad} failed`
          : html`Sent ${ok} ${ok === 1 ? "reply" : "replies"}.`}
      </div>
    `;
  }

  const selectedRows = state.buyers.filter(
    (b) => b.conv_id != null && state.selectedConvIds.has(b.conv_id),
  );
  const dedupe = dedupeForBulk(selectedRows);
  const sendCount = dedupe.totalOut;
  const skipped   = dedupe.duplicateCount;

  return html`
    <div class="${cls}">
      <span class="bulk-bar__count">
        ${count} selected
        ${skipped > 0 ? html`
          <span class="bulk-bar__dup-hint" title=${dupTooltip(dedupe)}>
            · ${skipped} dupe${skipped === 1 ? "" : "s"} skipped
          </span>
        ` : ""}
      </span>
      <select class="bulk-bar__select" id="bulk-template-select">
        <option value="">Pick a template…</option>
        ${state.templates.map((t) => html`
          <option value=${t.id} title=${t.body}>${t.label}</option>
        `)}
      </select>
      <button class="bulk-bar__send btn btn--primary btn--small" type="button"
              @click=${() => onSendClicked(state.templates, dedupe, false)}>
        Send to ${sendCount}
      </button>
      ${skipped > 0 ? html`
        <button class="bulk-bar__send-all btn btn--ghost btn--small" type="button"
                title="Send to every selected row, including the dupes"
                @click=${() => onSendClicked(state.templates, dedupe, true)}>
          Send to all ${count}
        </button>
      ` : ""}
      <button class="bulk-bar__archive btn btn--ghost btn--small" type="button"
              title="Archive selected buyers"
              @click=${() => {
                if (confirm(`Archive ${count} buyer${count === 1 ? "" : "s"}? You can restore them from the Archived tab.`)) {
                  void store.archiveSelected();
                }
              }}>
        Archive ${count}
      </button>
      <button class="bulk-bar__cancel btn btn--ghost btn--small" type="button"
              @click=${() => store.clearSelection()}>
        Cancel
      </button>
    </div>
  `;
}

function dupTooltip(d: DedupeResult): string {
  if (d.groups.length === 0) return "";
  const lines = d.groups.map((g) => {
    const others = g.dups.map((b) => `${b.display_name} (${b.account_username ?? "?"})`).join(", ");
    return `• ${g.primary.display_name} (${g.primary.account_username ?? "?"}) — also ${others} [matched on ${g.matchedBy}]`;
  });
  return `Sending to the most-recent thread per person:\n${lines.join("\n")}`;
}

function onSendClicked(templates: Template[], dedupe: DedupeResult, sendAll: boolean): void {
  const sel = document.getElementById("bulk-template-select") as HTMLSelectElement | null;
  if (!sel) return;
  const tpl = templates.find((t) => t.id === sel.value);
  if (!tpl) {
    sel.style.borderColor = "var(--accent-red)";
    setTimeout(() => { sel.style.borderColor = ""; }, 1200);
    return;
  }
  // Confirm any [PLACEHOLDER] tokens are intentional (bulk sends with
  // literal "[PRICE]" in the body would embarrass us). Block if found.
  if (/\[[A-Z]+\]/.test(tpl.body)) {
    alert(
      `This template still has placeholder tokens (e.g., ${tpl.body.match(/\[[A-Z]+\]/)?.[0]}).\n\n` +
      `Bulk sends won't fill those in. Pick a template without [BRACKETS] or send individually.`,
    );
    return;
  }
  const ids: string[] = sendAll
    ? collectIds([...dedupe.unique, ...dedupe.groups.flatMap((g) => g.dups)])
    : collectIds(dedupe.unique);
  if (ids.length === 0) return;
  void store.sendBulkReply(tpl.body, { ids });
}

function collectIds(rows: BuyerRow[]): string[] {
  const out: string[] = [];
  for (const r of rows) if (r.conv_id) out.push(r.conv_id);
  return out;
}
